/**
 * Cookie-based storage adapter for Supabase Auth.
 *
 * Allows multiple apps on different subdomains (or localhost ports) to share
 * a single Supabase session by storing auth tokens in a cookie with a shared
 * domain instead of origin-scoped localStorage.
 *
 * Structurally compatible with Supabase's `SupportedStorage` interface without
 * importing `@supabase/supabase-js`.
 */

import { decodeJwtPayload } from './jwt';

export interface SupportedStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CookieStorageOptions {
  /** Cookie domain (e.g. `.artic.works`). Omit for localhost / same-origin. */
  domain?: string;
  /** Max-age in seconds. Defaults to 400 days (34_560_000). */
  maxAge?: number;
}

export interface SupabaseSessionTokens {
  accessToken: string;
  refreshToken: string;
  previousRefreshToken: string;
}

const DEFAULT_MAX_AGE = 34_560_000; // ~400 days

/**
 * Max raw characters per cookie chunk.
 * After URL-encoding + key + attributes, this stays well under the 4 096-byte
 * browser limit for Supabase session JSON (mostly base64url JWTs).
 */
const CHUNK_SIZE = 3072;

function isSecureContext(): boolean {
  return typeof location !== 'undefined' && location.protocol === 'https:';
}

function parseCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  if (typeof document === 'undefined') return cookies;
  for (const pair of document.cookie.split(';')) {
    const [rawKey, ...rest] = pair.split('=');
    const key = rawKey?.trim();
    if (!key) continue;
    cookies[key] = decodeURIComponent(rest.join('='));
  }
  return cookies;
}

/** Build cookie attribute string from options. */
function cookieAttrs(maxAge: number, domain?: string): string {
  let attrs = `; path=/; max-age=${String(maxAge)}; SameSite=Lax`;
  if (domain) attrs += `; domain=${domain}`;
  if (isSecureContext()) attrs += '; Secure';
  return attrs;
}

/**
 * Expire any host-only shadow of `name` (cookie stored without a domain
 * attribute) plus all its chunk shadows. Domain-scoped writes can't
 * overwrite host-only entries, and `document.cookie` reads return the
 * most-specific match, so a stale shadow keeps winning every refresh.
 * Sentry ARTIC-HUB-PZ.
 */
function expireHostOnlyShadow(key: string): void {
  const attrs = cookieAttrs(0);
  document.cookie = `${key}=${attrs}`;
  const cookies = parseCookies();
  for (let i = 0; ; i++) {
    const chunkKey = `${key}.${String(i)}`;
    if (!(chunkKey in cookies)) break;
    document.cookie = `${chunkKey}=${attrs}`;
  }
}

/** Read a value from cookies, reassembling chunks if needed. */
function getCookieValue(key: string): string | null {
  const cookies = parseCookies();

  // Single (non-chunked) cookie
  if (key in cookies) return cookies[key] ?? null;

  // Chunked cookies: key.0, key.1, …
  const chunks: string[] = [];
  for (let i = 0; ; i++) {
    const chunk = cookies[`${key}.${String(i)}`];
    if (chunk === undefined) break;
    chunks.push(chunk);
  }
  return chunks.length > 0 ? chunks.join('') : null;
}

/** Write a value to cookies, chunking if it exceeds CHUNK_SIZE. */
function setCookieValue(key: string, value: string, maxAge: number, domain?: string): void {
  const attrs = cookieAttrs(maxAge, domain);

  if (domain) expireHostOnlyShadow(key);

  if (value.length <= CHUNK_SIZE) {
    // Clean up stale chunks from a previous larger value
    deleteCookieChunks(key, domain);
    document.cookie = `${key}=${encodeURIComponent(value)}${attrs}`;
    return;
  }

  // Value too large for one cookie — chunk it
  // Remove the non-chunked cookie first
  document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;

  const chunks: string[] = [];
  for (let i = 0; i < value.length; i += CHUNK_SIZE) {
    chunks.push(value.slice(i, i + CHUNK_SIZE));
  }

  for (const [i, chunk] of chunks.entries()) {
    document.cookie = `${key}.${String(i)}=${encodeURIComponent(chunk)}${attrs}`;
  }

  // Remove leftover chunks from a previously longer value
  const cookies = parseCookies();
  for (let i = chunks.length; ; i++) {
    if (!(`${key}.${String(i)}` in cookies)) break;
    document.cookie = `${key}.${String(i)}=; path=/; max-age=0; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
  }
}

/** Delete a cookie key and any associated chunks. */
function deleteCookieKey(key: string, domain?: string): void {
  document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
  deleteCookieChunks(key, domain);
  if (domain) expireHostOnlyShadow(key);
}

function deleteCookieChunks(key: string, domain?: string): void {
  const cookies = parseCookies();
  for (let i = 0; ; i++) {
    if (!(`${key}.${String(i)}` in cookies)) break;
    document.cookie = `${key}.${String(i)}=; path=/; max-age=0; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
  }
}

export function createCookieStorage(options: CookieStorageOptions = {}): SupportedStorage {
  const { domain, maxAge = DEFAULT_MAX_AGE } = options;

  return {
    getItem(key: string): string | null {
      return getCookieValue(key);
    },

    setItem(key: string, value: string): void {
      setCookieValue(key, value, maxAge, domain);
    },

    removeItem(key: string): void {
      deleteCookieKey(key, domain);
    },
  };
}

/**
 * Dual storage adapter: localStorage (reliable, no size limit) + chunked
 * cookies (cross-subdomain sharing).
 *
 * Reads from localStorage first; falls back to cookies so a different
 * subdomain can bootstrap a session from the shared cookie.
 * Writes to both, ensuring the Authorization header always works (Supabase
 * reads from this storage and sets the header internally) while cookies
 * stay in sync for cross-subdomain session hopping.
 */
export function createDualStorage(options: CookieStorageOptions = {}): SupportedStorage {
  const { domain, maxAge = DEFAULT_MAX_AGE } = options;

  return {
    getItem(key: string): string | null {
      // Primary: localStorage — reliable, no size limits
      try {
        const ls = localStorage.getItem(key);
        if (ls) return ls;
      } catch {
        // localStorage unavailable (SSR, private browsing edge-cases)
      }

      // Fallback: cookies — enables cross-subdomain session sharing
      return getCookieValue(key);
    },

    setItem(key: string, value: string): void {
      // Always write to localStorage (primary)
      try {
        localStorage.setItem(key, value);
      } catch {
        // quota exceeded or unavailable — cookie will still work
      }

      // Best-effort cookie sync for cross-subdomain sharing
      try {
        setCookieValue(key, value, maxAge, domain);
      } catch {
        // cookie write failed — localStorage still has the value
      }
    },

    removeItem(key: string): void {
      try {
        localStorage.removeItem(key);
      } catch {
        // localStorage may be unavailable (SSR, private browsing)
      }
      deleteCookieKey(key, domain);
    },
  };
}

/**
 * One-time, idempotent migration of Supabase auth tokens from localStorage
 * to cookie storage. Also migrates the PKCE code-verifier key.
 *
 * Best-effort — never throws.
 */
export function migrateLocalStorageToCookies(
  storageKey: string,
  cookieStorage: SupportedStorage,
): void {
  try {
    // Main auth token
    const value = localStorage.getItem(storageKey);
    if (value && !cookieStorage.getItem(storageKey)) {
      cookieStorage.setItem(storageKey, value);
      localStorage.removeItem(storageKey);
    } else if (value && cookieStorage.getItem(storageKey)) {
      // Cookie already exists — just clean up localStorage
      localStorage.removeItem(storageKey);
    }

    // PKCE code-verifier
    const codeVerifierKey = `${storageKey}-code-verifier`;
    const codeVerifier = localStorage.getItem(codeVerifierKey);
    if (codeVerifier && !cookieStorage.getItem(codeVerifierKey)) {
      cookieStorage.setItem(codeVerifierKey, codeVerifier);
      localStorage.removeItem(codeVerifierKey);
    } else if (codeVerifier && cookieStorage.getItem(codeVerifierKey)) {
      localStorage.removeItem(codeVerifierKey);
    }
  } catch {
    // Best-effort — localStorage or cookies may be unavailable
  }
}

/**
 * Ensures both sides of dual storage are in sync.
 *
 * Handles all prior states:
 * - localStorage-only (pre-cookie migration) → syncs to cookies
 * - Cookie-only (post migrateLocalStorageToCookies) → restores to localStorage
 * - Both present → no-op
 *
 * Best-effort — never throws.
 */
export function ensureDualStorageSync(storageKey: string, storage: SupportedStorage): void {
  try {
    // getItem reads localStorage first, then cookies.
    // setItem writes to both. This one call heals any single-side state.
    const value = storage.getItem(storageKey);
    if (value) {
      storage.setItem(storageKey, value);
    }

    const codeVerifierKey = `${storageKey}-code-verifier`;
    const codeVerifier = storage.getItem(codeVerifierKey);
    if (codeVerifier) {
      storage.setItem(codeVerifierKey, codeVerifier);
    }
  } catch {
    // Best-effort
  }
}

function readJwtIdentity(token: string): { sub: string; exp: number } | null {
  const payload = decodeJwtPayload(token);
  return payload &&
    typeof payload['sub'] === 'string' &&
    typeof payload['exp'] === 'number' &&
    Number.isFinite(payload['exp'])
    ? { sub: payload['sub'], exp: payload['exp'] }
    : null;
}

/**
 * Reconcile Supabase's persisted session with a token pair refreshed by a
 * cookie-only microsite. This must run before the Supabase client initializes:
 * otherwise auth-js can consume the stale, single-use refresh token first.
 *
 * The persisted user is retained only when the JWT belongs to that same user,
 * and an older cookie token can never replace a newer Supabase session.
 */
export function reconcileSupabaseSessionTokens(
  storageKey: string,
  storage: SupportedStorage,
  tokens: SupabaseSessionTokens,
): boolean {
  try {
    const value = storage.getItem(storageKey);
    if (!value) return false;

    const session = JSON.parse(value) as Record<string, unknown>;
    if (!session || typeof session !== 'object' || Array.isArray(session)) return false;

    const user = session['user'];
    if (!user || typeof user !== 'object' || Array.isArray(user)) return false;
    const userId = (user as Record<string, unknown>)['id'];

    const incomingIdentity = readJwtIdentity(tokens.accessToken);
    if (!incomingIdentity || incomingIdentity.sub !== userId) return false;

    const storedAccessToken = session['access_token'];
    const storedIdentity =
      typeof storedAccessToken === 'string' ? readJwtIdentity(storedAccessToken) : null;
    const tokenPairChanged =
      storedAccessToken !== tokens.accessToken || session['refresh_token'] !== tokens.refreshToken;
    if (tokenPairChanged && session['refresh_token'] !== tokens.previousRefreshToken) return false;
    if (storedIdentity && tokenPairChanged && incomingIdentity.exp <= storedIdentity.exp)
      return false;

    if (
      storedAccessToken === tokens.accessToken &&
      session['refresh_token'] === tokens.refreshToken &&
      session['expires_at'] === incomingIdentity.exp
    ) {
      return false;
    }

    storage.setItem(
      storageKey,
      JSON.stringify({
        ...session,
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
        expires_at: incomingIdentity.exp,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * App-specific localStorage keys that should be cleared on sign-out.
 * Supabase manages its own cookie via `signOut()`.
 */
export const APP_LOCAL_STORAGE_KEYS = [
  'current_partner',
  'current_factory',
  'station_sort_preferences',
  'workOrderManager_data',
  'upgradeErrorReloadTimestamp',
  'i18nextLng',
] as const;

/**
 * Removes only app-specific localStorage keys.
 * Use instead of `localStorage.clear()` so Supabase cookie auth is unaffected.
 */
export function clearAppLocalStorage(): void {
  try {
    for (const key of APP_LOCAL_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable (SSR, private browsing edge-cases)
  }
}
